/**
 * NewsletterPreferences.js
 * Subscriber preference centre — token-gated via URL param.
 * URL: /newsletter/preferences?token=X
 *
 * Lets subscribers update their role, platform, pain-point answers
 * and also request permanent data deletion (GDPR Article 17).
 */
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Container, Button, CircularProgress, Alert,
    Stack, Chip, Divider, Dialog, DialogTitle, DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Save as SaveIcon,
    Delete as DeleteIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    AutoAwesome as SparkleIcon,
    Group as GroupIcon,
    Twitter as TwitterIcon,
    TrendingUp as TrendingIcon,
    Lightbulb as IdeaIcon,
    Schedule as ScheduleIcon,
    Analytics as AnalyticsIcon,
    Forum as EngageIcon,
} from '@mui/icons-material';
import { newsletterApi } from '../services/api';

// ── Constants (same as NewsletterSection) ────────────────────────────────────

const ROLES = [
    { id: 'agency',          label: 'Agency Owner',         icon: '🏢' },
    { id: 'social_manager',  label: 'Social Media Manager', icon: '📱' },
    { id: 'business_owner',  label: 'Business Owner',       icon: '💼' },
    { id: 'content_creator', label: 'Content Creator',      icon: '🎨' },
];

const PLATFORMS = [
    { id: 'instagram', label: 'Instagram', icon: <InstagramIcon sx={{ fontSize: 18 }} /> },
    { id: 'youtube',   label: 'YouTube',   icon: <YouTubeIcon   sx={{ fontSize: 18 }} /> },
    { id: 'facebook',  label: 'Facebook',  icon: <FacebookIcon  sx={{ fontSize: 18 }} /> },
    { id: 'tiktok',    label: 'TikTok',    icon: <SparkleIcon   sx={{ fontSize: 18 }} /> },
    { id: 'linkedin',  label: 'LinkedIn',  icon: <GroupIcon     sx={{ fontSize: 18 }} /> },
    { id: 'twitter',   label: 'Twitter/X', icon: <TwitterIcon   sx={{ fontSize: 18 }} /> },
];

const PAIN_POINTS = [
    { id: 'ideas',      label: 'Coming up with ideas',    icon: <IdeaIcon      sx={{ fontSize: 20 }} /> },
    { id: 'scheduling', label: 'Consistent scheduling',   icon: <ScheduleIcon  sx={{ fontSize: 20 }} /> },
    { id: 'analytics',  label: 'Understanding analytics', icon: <AnalyticsIcon sx={{ fontSize: 20 }} /> },
    { id: 'engagement', label: 'Growing engagement',      icon: <EngageIcon    sx={{ fontSize: 20 }} /> },
    { id: 'all',        label: 'All of the above 😅',     icon: <TrendingIcon  sx={{ fontSize: 20 }} /> },
];

function SelectCard({ selected, onClick, children }) {
    return (
        <Box onClick={onClick} sx={{
            p: 2, borderRadius: '12px', cursor: 'pointer',
            border: `1.5px solid ${selected ? '#6366f1' : 'rgba(255,255,255,0.08)'}`,
            bgcolor: selected ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.02)',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'rgba(99,102,241,0.4)' },
        }}>
            {children}
        </Box>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NewsletterPreferences() {
    const token = new URLSearchParams(window.location.search).get('token') || '';

    const [loading,    setLoading]    = useState(true);
    const [saving,     setSaving]     = useState(false);
    const [deleting,   setDeleting]   = useState(false);
    const [notFound,   setNotFound]   = useState(false);
    const [success,    setSuccess]    = useState(false);
    const [deleted,    setDeleted]    = useState(false);
    const [error,      setError]      = useState(null);
    const [confirmDel, setConfirmDel] = useState(false);

    const [email,     setEmail]     = useState('');
    const [status,    setStatus]    = useState('');
    const [, setRefLink]   = useState('');
    const [role,      setRole]      = useState('');
    const [platforms, setPlatforms] = useState([]);
    const [painPoint, setPainPoint] = useState('');

    useEffect(() => {
        if (!token) { setNotFound(true); setLoading(false); return; }
        newsletterApi.getPreferences(token)
            .then(data => {
                setEmail(data.email || '');
                setStatus(data.status || '');
                setRefLink(data.refLink || '');
                setRole(data.role || '');
                setPlatforms(data.platforms || []);
                setPainPoint(data.painPoint || '');
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [token]);

    const togglePlatform = id =>
        setPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

    const handleSave = async () => {
        setSaving(true); setError(null); setSuccess(false);
        try {
            await newsletterApi.updatePreferences(token, { role, platforms, painPoint });
            setSuccess(true);
            setTimeout(() => setSuccess(false), 4000);
        } catch {
            setError('Failed to save preferences. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setConfirmDel(false);
        setDeleting(true); setError(null);
        try {
            await newsletterApi.deleteMyData(token);
            setDeleted(true);
        } catch {
            setError('Failed to delete your data. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const cardSx = {
        p: { xs: 4, md: 6 }, borderRadius: '24px',
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a14', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
        );
    }

    if (notFound) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a14', py: 12 }}>
                <Container maxWidth="sm">
                    <Box sx={{ ...cardSx, textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 900, mb: 2 }}>Link not found</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
                            This preferences link is invalid or has expired.
                        </Typography>
                        <Button href="/" sx={{ color: '#6366f1' }}>← Back to Socialixer</Button>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (deleted) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a14', py: 12 }}>
                <Container maxWidth="sm">
                    <Box sx={{ ...cardSx, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '3rem', mb: 2 }}>👋</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 900, mb: 2 }}>Data deleted</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
                            Your email and all associated data have been permanently erased.
                            We're sorry to see you go.
                        </Typography>
                        <Button href="/" sx={{ color: '#6366f1' }}>← Back to Socialixer</Button>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a14', py: { xs: 6, md: 12 } }}>
            <Container maxWidth="sm">
                <Box sx={cardSx}>
                    {/* Header */}
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                        <Box sx={{
                            width: 42, height: 42, borderRadius: '12px',
                            background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 900, fontSize: '1.2rem',
                        }}>S</Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 900 }}>Email Preferences</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{email}</Typography>
                        </Box>
                        {status && (
                            <Chip
                                label={status}
                                size="small"
                                sx={{ ml: 'auto', bgcolor: status === 'active' ? 'rgba(16,217,138,0.15)' : 'rgba(255,255,255,0.08)', color: status === 'active' ? '#10d98a' : 'rgba(255,255,255,0.5)', fontWeight: 700, textTransform: 'capitalize' }}
                            />
                        )}
                    </Stack>

                    {/* Role */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                            What best describes you?
                        </Typography>
                        <Stack spacing={1.5}>
                            {ROLES.map(r => (
                                <SelectCard key={r.id} selected={role === r.id} onClick={() => setRole(r.id)}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Box sx={{ fontSize: '1.3rem' }}>{r.icon}</Box>
                                        <Typography sx={{ fontWeight: 700, color: 'white' }}>{r.label}</Typography>
                                    </Stack>
                                </SelectCard>
                            ))}
                        </Stack>
                    </Box>

                    {/* Platforms */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                            Which platforms do you use?
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {PLATFORMS.map(p => (
                                <SelectCard key={p.id} selected={platforms.includes(p.id)} onClick={() => togglePlatform(p.id)}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
                                        <Box sx={{ color: platforms.includes(p.id) ? '#6366f1' : 'rgba(255,255,255,0.5)' }}>{p.icon}</Box>
                                        <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>{p.label}</Typography>
                                    </Stack>
                                </SelectCard>
                            ))}
                        </Box>
                    </Box>

                    {/* Pain point */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                            Biggest challenge?
                        </Typography>
                        <Stack spacing={1.5}>
                            {PAIN_POINTS.map(p => (
                                <SelectCard key={p.id} selected={painPoint === p.id} onClick={() => setPainPoint(p.id)}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Box sx={{ color: painPoint === p.id ? '#6366f1' : 'rgba(255,255,255,0.4)' }}>{p.icon}</Box>
                                        <Typography sx={{ fontWeight: 700, color: 'white' }}>{p.label}</Typography>
                                    </Stack>
                                </SelectCard>
                            ))}
                        </Stack>
                    </Box>

                    {success && (
                        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px', bgcolor: 'rgba(16,217,138,0.08)', color: '#10d98a', border: '1px solid rgba(16,217,138,0.2)' }}>
                            Preferences saved successfully!
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                            {error}
                        </Alert>
                    )}

                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        variant="contained"
                        fullWidth
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        sx={{ height: 52, borderRadius: '100px', bgcolor: '#6366f1', fontWeight: 800, mb: 2, '&:hover': { bgcolor: '#818cf8' } }}
                    >
                        {saving ? 'Saving…' : 'Save Preferences'}
                    </Button>

                    <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

                    {/* GDPR Erasure */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'rgba(255,255,255,0.5)' }}>
                            Data & Privacy
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mb: 2, lineHeight: 1.6 }}>
                            Under GDPR Article 17 you have the right to erasure. Clicking below permanently
                            deletes your email and all associated data from our systems.
                        </Typography>
                        <Button
                            onClick={() => setConfirmDel(true)}
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                            sx={{ color: '#ef4444', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}
                        >
                            {deleting ? 'Deleting…' : 'Delete my data permanently'}
                        </Button>
                    </Box>
                </Box>
            </Container>

            {/* Confirm delete dialog */}
            <Dialog open={confirmDel} onClose={() => setConfirmDel(false)}
                PaperProps={{ sx: { bgcolor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px' } }}>
                <DialogTitle sx={{ color: 'white', fontWeight: 800 }}>Delete your data?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        This will permanently erase <strong style={{ color: 'white' }}>{email}</strong> and all associated
                        data from Socialixer. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfirmDel(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>
                        Yes, delete everything
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
